(() => {
  'use strict';

  const TAG_NAME = 'runit-super-creator-bundle';

  const BUNDLE_ID_PROPERTY = '_RunIt bundle ID';
  const BUNDLE_ROLE_PROPERTY = '_RunIt bundle role';
  const DISCOUNT_CODE_PROPERTY = '_RunIt bundle discount code';

  function normalizeCode(code) {
    return String(code || '').trim().toUpperCase();
  }

  function getStoreRootRoute() {
    return window.Shopify &&
      window.Shopify.routes &&
      window.Shopify.routes.root
      ? window.Shopify.routes.root
      : '/';
  }

  async function fetchCart() {
    const response = await fetch(getStoreRootRoute() + 'cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    });

    if (!response.ok) {
      throw new Error('Unable to load the cart.');
    }

    return response.json();
  }

  function cartDiscountCodes(cart) {
    const codes = new Set(
      (cart.discount_codes || [])
        .map((discount) => normalizeCode(discount.code))
        .filter(Boolean)
    );

    const rememberApplication = (application) => {
      if (!application || application.type !== 'discount_code') {
        return;
      }

      const code = normalizeCode(application.title);

      if (code) {
        codes.add(code);
      }
    };

    (cart.cart_level_discount_applications || []).forEach(
      rememberApplication
    );

    (cart.items || []).forEach((item) => {
      (item.line_level_discount_allocations || []).forEach((allocation) => {
        rememberApplication(allocation.discount_application);
      });
    });

    return Array.from(codes);
  }

  function cartHasCodeDiscount(cart, code) {
    return cartDiscountCodes(cart).includes(normalizeCode(code));
  }

  function bundleLineDiscountTotal(cart, bundleId) {
    if (!bundleId) {
      return 0;
    }

    return (cart.items || []).reduce((total, item) => {
      const properties = item.properties || {};

      if (String(properties[BUNDLE_ID_PROPERTY] || '') !== bundleId) {
        return total;
      }

      return total + Number(
        item.line_level_total_discount || item.total_discount || 0
      );
    }, 0);
  }

  async function postDiscountCodes(codes) {
    const response = await fetch(getStoreRootRoute() + 'cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ discount: codes.join(',') })
    });

    if (!response.ok) {
      throw new Error('Unable to update the bundle discount.');
    }

    return response.json();
  }

  if (customElements.get(TAG_NAME)) {
    return;
  }

  class RunItSuperCreatorBundle extends HTMLElement {
    connectedCallback() {
      if (this.dataset.scReady === 'true') {
        return;
      }

      this.dataset.scReady = 'true';
      this.retryCount = 0;
      this.adding = false;
      this.disabledForRoof = false;

      this.boundOfferChange = this.handleOfferChange.bind(this);
      this.boundRoofChange = this.handleRoofChange.bind(this);
      this.boundButtonClick = this.handleButtonClick.bind(this);
      this.boundFormSubmit = this.handleFormSubmit.bind(this);
      this.boundDocumentChange = this.handleDocumentChange.bind(this);

      this.initialize();
    }

    disconnectedCallback() {
      window.clearTimeout(this.retryTimer);

      if (this.offerInputs) {
        this.offerInputs.forEach((input) => {
          input.removeEventListener('change', this.boundOfferChange);
        });
      }

      if (this.roofInputs) {
        this.roofInputs.forEach((input) => {
          input.removeEventListener('change', this.boundRoofChange);
        });
      }

      if (this.button) {
        this.button.removeEventListener('click', this.boundButtonClick, true);
      }

      if (this.form) {
        this.form.removeEventListener('submit', this.boundFormSubmit, true);
      }

      document.removeEventListener('change', this.boundDocumentChange);
    }

    findProductForm() {
      const quickAdd = this.closest('[data-quick-add-modal]');

      if (quickAdd) {
        const forms = Array.from(
          quickAdd.querySelectorAll(
            'form[data-product-form], product-form form, form[action*="/cart/add"]'
          )
        );
        const expectedId = this.dataset.formId || '';
        const matchingForm = forms.find((form) => form.id === expectedId);

        return matchingForm || forms[0] || null;
      }

      return document.getElementById(this.dataset.formId);
    }

    initializeWithForm(form) {
      this.retryCount = 0;
      this.initialize(form || null);
    }

    initialize(formOverride = null) {
      const nextForm = formOverride || this.findProductForm();

      if (
        this.dataset.scInitialized === 'true' &&
        this.form === nextForm
      ) {
        this.sync();
        return;
      }

      this.form = nextForm;

      if (!this.form) {
        this.retryCount += 1;

        if (this.retryCount <= 20) {
          this.retryTimer = window.setTimeout(() => this.initialize(), 100);
        }

        return;
      }

      this.button = this.form.querySelector('[data-add-to-cart]');
      this.buttonText = this.button
        ? this.button.querySelector('[data-add-to-cart-text]')
        : null;

      if (!this.button) {
        return;
      }

      this.dataset.scInitialized = 'true';

      this.variants = this.readJson('[data-sc-current-variants]');
      this.offerInputs = Array.from(this.querySelectorAll('[data-sc-offer]'));
      this.roofInputs = Array.from(this.querySelectorAll('[data-sc-roof]'));
      this.details = this.querySelector('[data-sc-details]');
      this.error = this.querySelector('[data-sc-error]');
      this.singlePrice = this.querySelector('[data-sc-single-price]');
      this.bundlePrice = this.querySelector('[data-sc-bundle-price]');
      this.originalPrice = this.querySelector('[data-sc-original-price]');
      this.currentMiniPrice = this.querySelector('[data-sc-current-mini]');
      this.originalButtonHtml = this.buttonText ? this.buttonText.innerHTML : '';

      const uniqueSuffix = this.id || String(Date.now());
      this.offerInputs.forEach((input) => {
        input.name = 'runit-bundle-' + uniqueSuffix;
        input.addEventListener('change', this.boundOfferChange);
      });

      this.roofInputs.forEach((input) => {
        input.name = 'runit-roof-' + uniqueSuffix;
        input.addEventListener('change', this.boundRoofChange);
      });

      this.button.addEventListener('click', this.boundButtonClick, true);
      this.form.addEventListener('submit', this.boundFormSubmit, true);
      document.addEventListener('change', this.boundDocumentChange);

      this.sync();
    }

    readJson(selector) {
      const script = this.querySelector(selector);

      if (!script) {
        return [];
      }

      try {
        return JSON.parse(script.textContent);
      } catch (error) {
        return [];
      }
    }

    getRootRoute() {
      return window.Shopify &&
        Shopify.routes &&
        Shopify.routes.root
        ? Shopify.routes.root
        : '/';
    }

    getCurrentVariant() {
      const input = this.form
        ? this.form.querySelector('[name="id"]')
        : null;

      const id = input ? String(input.value) : '';

      return (
        this.variants.find((variant) => String(variant.id) === id) ||
        this.variants[0] ||
        null
      );
    }

    getSelectedOffer() {
      return this.offerInputs.find((input) => input.checked) || null;
    }

    isCompleteSelected() {
      const selected = this.getSelectedOffer();
      return !!selected && selected.value === 'complete';
    }

    getSelectedRoof() {
      return this.roofInputs.find((input) => input.checked) || null;
    }

    getDisplayRoof() {
      return this.getSelectedRoof() || this.roofInputs[0] || null;
    }

    formatMoney(cents) {
      const amount = Number(cents || 0) / 100;

      try {
        return (
          'PHP ' +
          new Intl.NumberFormat('en-PH', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }).format(amount)
        );
      } catch (error) {
        return 'PHP ' + amount.toFixed(2);
      }
    }

    calculateTotals(roof) {
      const variant = this.getCurrentVariant();
      const currentPrice = variant ? Number(variant.price || 0) : 0;
      const doorPrice = Number(this.dataset.doorPrice || 0);
      const roofPrice = roof ? Number(roof.dataset.price || 0) : 0;
      const discount = Number(this.dataset.discount || 0);
      const subtotal = currentPrice + doorPrice + roofPrice;

      return {
        subtotal,
        total: Math.max(0, subtotal - discount)
      };
    }

    updatePrices() {
      const variant = this.getCurrentVariant();

      if (!variant) {
        return;
      }

      if (this.singlePrice) {
        this.singlePrice.textContent = this.formatMoney(variant.price);
      }

      if (this.currentMiniPrice) {
        this.currentMiniPrice.textContent = this.formatMoney(variant.price);
      }

      const roof = this.getDisplayRoof();
      const totals = this.calculateTotals(roof);

      if (this.bundlePrice) {
        this.bundlePrice.textContent = this.formatMoney(totals.total);
      }

      if (this.originalPrice) {
        this.originalPrice.textContent =
          this.formatMoney(totals.subtotal);
      }
    }

    updateSelectedStyles() {
      this.querySelectorAll('[data-sc-option]').forEach((option) => {
        const input = option.querySelector('[data-sc-offer]');
        option.classList.toggle('is-selected', !!input && input.checked);
      });

      this.querySelectorAll('[data-sc-roof-card]').forEach((card) => {
        const input = card.querySelector('[data-sc-roof]');
        card.classList.toggle('is-selected', !!input && input.checked);
      });
    }

    updateButton() {
      if (!this.button || !this.buttonText) {
        return;
      }

      const variant = this.getCurrentVariant();
      const variantUnavailable = !variant || variant.available === false;
      const complete = this.isCompleteSelected();
      const roof = this.getSelectedRoof();

      if (complete && !roof) {
        this.disabledForRoof = true;
        this.button.disabled = true;
        this.buttonText.textContent =
          this.dataset.prompt || 'CHOOSE A ROOF TO CONTINUE';
        return;
      }

      if (this.disabledForRoof) {
        this.button.disabled = variantUnavailable;
        this.disabledForRoof = false;
      }

      if (complete && roof) {
        const totals = this.calculateTotals(roof);
        this.buttonText.textContent =
          'ADD COMPLETE KIT — ' + this.formatMoney(totals.total);
      } else {
        this.buttonText.innerHTML = this.originalButtonHtml;
      }
    }

    sync() {
      const complete = this.isCompleteSelected();

      if (this.details) {
        this.details.hidden = !complete;
      }

      if (this.error) {
        this.error.hidden = true;
        this.error.textContent = '';
      }

      this.updateSelectedStyles();
      this.updatePrices();
      this.updateButton();
    }

    handleOfferChange() {
      this.sync();

      if (
        this.isCompleteSelected() &&
        !this.getSelectedRoof() &&
        this.roofInputs.length
      ) {
        window.setTimeout(() => {
          this.roofInputs[0].focus();
        }, 0);
      }
    }

    handleRoofChange() {
      this.sync();
    }

    handleDocumentChange(event) {
      if (
        event.target &&
        event.target.name === 'id' &&
        event.target.form === this.form
      ) {
        window.setTimeout(() => this.sync(), 80);
      }
    }

    handleButtonClick(event) {
      if (!this.isCompleteSelected()) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      this.addBundle();
    }

    handleFormSubmit(event) {
      if (!this.isCompleteSelected()) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      this.addBundle();
    }

    getCurrentProductProperties() {
      const properties = {};
      const fields = document.querySelectorAll(
        'input[name^="properties["], ' +
        'select[name^="properties["], ' +
        'textarea[name^="properties["]'
      );

      fields.forEach((field) => {
        if (field.disabled || field.form !== this.form || field.type === 'file') {
          return;
        }

        if (
          (field.type === 'checkbox' || field.type === 'radio') &&
          !field.checked
        ) {
          return;
        }

        const name = field.getAttribute('name');
        const match = name && name.match(/^properties\[(.+)\]$/);

        if (!match || field.value === '') {
          return;
        }

        properties[match[1]] = String(field.value);
      });

      return properties;
    }

    buildItems() {
      const variant = this.getCurrentVariant();
      const roof = this.getSelectedRoof();
      const doorVariantId = Number(this.dataset.doorVariantId || 0);
      const discountCode = normalizeCode(this.dataset.discountCode);

      if (!variant || !variant.id || !roof || !doorVariantId || !discountCode) {
        return [];
      }

      const bundleId =
        'SC-' +
        Date.now() +
        '-' +
        Math.random().toString(36).slice(2, 8);

      this.pendingBundleId = bundleId;

      const mainProperties = this.getCurrentProductProperties();
      mainProperties.Bundle =
        this.dataset.bundleLabel || 'Complete Your Kit';
      mainProperties.Roof = roof.dataset.productTitle || 'Selected roof';
      mainProperties[BUNDLE_ID_PROPERTY] = bundleId;
      mainProperties[BUNDLE_ROLE_PROPERTY] = 'Kit';
      mainProperties[DISCOUNT_CODE_PROPERTY] = discountCode;

      const mainItem = {
        id: Number(variant.id),
        quantity: 1,
        properties: mainProperties
      };

      const sellingPlan = this.form.querySelector('[name="selling_plan"]');

      if (sellingPlan && sellingPlan.value) {
        mainItem.selling_plan = Number(sellingPlan.value);
      }

      return [
        mainItem,
        {
          id: doorVariantId,
          quantity: 1,
          properties: {
            [BUNDLE_ID_PROPERTY]: bundleId,
            [BUNDLE_ROLE_PROPERTY]: 'Door',
            [DISCOUNT_CODE_PROPERTY]: discountCode
          }
        },
        {
          id: Number(roof.value),
          quantity: 1,
          properties: {
            [BUNDLE_ID_PROPERTY]: bundleId,
            [BUNDLE_ROLE_PROPERTY]: 'Roof',
            [DISCOUNT_CODE_PROPERTY]: discountCode
          }
        }
      ];
    }

    showError(message) {
      if (!this.error) {
        window.alert(message);
        return;
      }

      this.error.textContent = message;
      this.error.hidden = false;
    }

    setLoading(loading) {
      if (!this.button || !this.buttonText) {
        return;
      }

      this.adding = loading;
      this.button.classList.toggle('is-loading', loading);

      if (loading) {
        this.button.disabled = true;
        this.buttonText.textContent = 'ADDING COMPLETE KIT...';
      } else {
        this.updateButton();
      }
    }


    async getCart() {
      return fetchCart();
    }

    async applyBundleDiscount(code) {
      const normalized = normalizeCode(code);

      if (!normalized) {
        throw new Error('The complete-kit discount is not configured.');
      }

      const cart = await this.getCart();
      const codes = cartDiscountCodes(cart);

      if (!codes.includes(normalized)) {
        codes.push(normalized);
      }

      const updatedCart = await postDiscountCodes(codes);
      const expectedDiscount = Math.max(
        1,
        Number(this.dataset.discount || 0)
      );
      const beforeTotalDiscount = Number(cart.total_discount || 0);
      const afterTotalDiscount = Number(updatedCart.total_discount || 0);
      const bundleDiscount = bundleLineDiscountTotal(
        updatedCart,
        this.pendingBundleId
      );
      const applied =
        cartHasCodeDiscount(updatedCart, normalized) ||
        bundleDiscount >= expectedDiscount ||
        afterTotalDiscount - beforeTotalDiscount >= expectedDiscount;

      if (!applied) {
        throw new Error('Shopify did not accept the complete-kit discount.');
      }

      return updatedCart;
    }

    async removeBundle(bundleId) {
      if (!bundleId) {
        return;
      }

      const cart = await this.getCart();
      const updates = {};

      (cart.items || []).forEach((item) => {
        const properties = item.properties || {};

        if (String(properties[BUNDLE_ID_PROPERTY] || '') === bundleId) {
          updates[item.key] = 0;
        }
      });

      if (!Object.keys(updates).length) {
        return;
      }

      await fetch(this.getRootRoute() + 'cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ updates })
      });
    }

    refreshCart(response) {
      this.button.dispatchEvent(
        new CustomEvent('theme:product:add', {
          detail: {
            response,
            button: this.button
          },
          bubbles: true
        })
      );

      const cartDrawer = document.querySelector('cart-drawer');
      const cartItems = document.querySelector('cart-items');
      const usesDrawer =
        window.theme &&
        window.theme.settings &&
        window.theme.settings.cartType === 'drawer';

      if (cartDrawer && cartItems && usesDrawer) {
        document.dispatchEvent(
          new CustomEvent('theme:cart:refresh', {
            bubbles: true
          })
        );

        return;
      }

      window.location.href = this.getRootRoute() + 'cart';
    }

    async addBundle() {
      if (this.adding) {
        return;
      }

      const roof = this.getSelectedRoof();

      if (!roof) {
        this.showError('Please choose a roof before adding the complete kit.');
        this.updateButton();

        if (this.roofInputs.length) {
          this.roofInputs[0].focus();
        }

        return;
      }

      const items = this.buildItems();
      const bundleId = this.pendingBundleId || '';

      if (items.length !== 3) {
        this.showError('The complete kit could not be prepared. Please try again.');
        return;
      }

      this.setLoading(true);

      let bundleAdded = false;

      try {
        const response = await fetch(this.getRootRoute() + 'cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ items })
        });

        if (!response.ok) {
          const error = await response.json();
          throw error;
        }

        const data = await response.json();
        bundleAdded = true;

        await this.applyBundleDiscount(this.dataset.discountCode);

        this.setLoading(false);
        this.refreshCart(data);
      } catch (error) {
        if (bundleAdded && bundleId) {
          try {
            await this.removeBundle(bundleId);
          } catch (rollbackError) {
            console.error(
              '[RunIt Super Creator Bundle] Rollback error:',
              rollbackError
            );
          }
        }

        console.error('[RunIt Super Creator Bundle] Add error:', error);
        this.setLoading(false);
        this.showError(
          error.description ||
          'The complete kit could not be added with its discount. Nothing was added. Please try again.'
        );
      }
    }
  }

  customElements.define(TAG_NAME, RunItSuperCreatorBundle);
})();
